/**
 * Suggested Ontology/Schema API client (Phase 1, T1.6).
 *
 * Typed client for the three additive backend endpoints (mounted only when the
 * backend `enable_suggested_schema` flag is on):
 *   - POST /api/ontology-suggestions/generate
 *   - GET  /api/ontology-suggestions/{id}
 *   - GET  /api/ontology-templates
 *
 * Uses the shared apiClient (baseURL already ends in `/api`), so paths here drop
 * the `/api` prefix. The backend identifies the user via the `X-User-Email`
 * header (mirrors case_analysis.get_current_user); we forward it per request.
 */

import apiClient from '../api';

// ---------------------------------------------------------------------------
// Types (mirror src/models/suggested_schema.py + schema_design.py shapes).
// ---------------------------------------------------------------------------

export type ProvenanceSource = 'seed' | 'ai';
export type ElementKind = 'entity' | 'relationship' | 'attribute';

/**
 * Optional cross-time join metadata for a relationship element (T3.5).
 * Carried on the NEW-side ElementProvenance (NOT on the shared relationship shape) so
 * existing payloads are unchanged when absent. `strategy` is advisory free-text
 * (point-in-time | interval-overlap | as-of | latest) so new strategies stay additive.
 */
export interface TemporalJoin {
  fromField?: string | null;
  toField?: string | null;
  strategy?: string | null;
  grain?: string | null;
  description?: string;
}

export interface ElementProvenance {
  elementType: ElementKind;
  elementKey: string;
  source: ProvenanceSource;
  confidence: number;
  confidenceKnown: boolean;
  rationale: string;
  /** T3.5 (optional): relationship cardinality mirrored for per-element display. */
  cardinality?: string | null;
  /** T3.5 (optional): cross-time join spec; absent => no temporal handling. */
  temporalJoin?: TemporalJoin | null;
}

/** A persisted quality-gate finding (T3.5): why an AI element was flagged/excluded. */
export interface QualityFindingRecord {
  kind:
    | 'low_confidence'
    | 'unknown_confidence'
    | 'duplicate_of_seed'
    | 'duplicate_ai'
    | 'dangling_endpoint'
    | 'duplicate_entity';
  elementType: ElementKind;
  elementKey: string;
  action: 'flag' | 'exclude';
  source: ProvenanceSource;
  detail?: string;
}

export interface SuggestedNodeProperty {
  name: string;
  type: string;
  required?: boolean;
  unique?: boolean;
  indexed?: boolean;
  description?: string | null;
}

export interface SuggestedNodeDefinition {
  label: string;
  category?: string | null;
  description: string;
  properties: SuggestedNodeProperty[];
}

export interface SuggestedRelationshipDefinition {
  type: string;
  /** Backend serializes with `from`/`to` aliases. */
  from: string;
  to: string;
  description: string;
  cardinality?: string | null;
  properties?: Array<{ name: string; type: string; required?: boolean; description?: string | null }>;
}

export interface SuggestedSchemaDesign {
  summary: string;
  nodes: SuggestedNodeDefinition[];
  relationships: SuggestedRelationshipDefinition[];
  indexes: any[];
  constraints: any[];
}

export type SuggestedSchemaStatus = 'generated' | 'accepted' | 'rejected' | 'superseded';

export interface SuggestedSchema {
  suggestionId: string;
  industry: string;
  domain: string;
  caseId?: string | null;
  runId?: string | null;
  status: SuggestedSchemaStatus;
  schemaDesign: SuggestedSchemaDesign;
  provenance: ElementProvenance[];
  overallConfidence: number;
  /** T3.5 (optional): quality-gate findings; empty/absent when nothing was flagged. */
  qualityFindings?: QualityFindingRecord[];
  cypherDdlPreview: string;
  contentHash: string;
  companyContext?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface OntologyTemplate {
  industry: string;
  industryName: string;
  domain: string;
  domainName: string;
  domainType: string;
}

export interface GenerateSuggestionRequest {
  industry: string;
  domain: string;
  /** Multiple KG-area domains for a comprehensive multi-domain schema (overrides
   *  `domain` server-side when set and multi-domain is enabled). */
  domains?: string[];
  caseId?: string;
  companyContext?: Record<string, unknown>;
}

/** Auto-detected (industry, domain) for a case, used to pre-fill the pickers. */
export interface ResolvedIndustryDomain {
  industry: string;
  industryName: string;
  domain: string;
  domainName: string;
  confidence: number;
  /** Where industry was inferred from: wizard | discovery | company | company_name | default. */
  source: string;
  companyName?: string | null;
}

/** Auto-detected industry + the FULL set of relevant KG-area domains for a case
 *  (multi-domain "comprehensive" schema). */
export interface ResolvedIndustryDomains {
  industry: string;
  industryName: string;
  domains: Array<{ domain: string; domainName: string; score?: number; source?: string }>;
  companyName?: string | null;
}

const BASE_URL = '/ontology-suggestions';

// Identity is currently resolved uniformly as the system user across the app:
// the shared apiClient never sends X-User-Email, so the backend defaults to
// system@graphsolution.ai (which passes the case-access bypass). This client
// used to forward the real X-User-Email from localStorage, which made it the
// ONLY caller to hit a real per-user access check — and it 403'd on cases that
// every other endpoint created/owned as the system user. Until real per-user
// access control is enabled app-wide, send no X-User-Email here so ontology
// suggestions resolve to the same system user as everything else.
// (Signature kept so call sites are untouched; userEmail is intentionally unused.)
function userHeaders(_userEmail?: string): Record<string, string> {
  return {};
}

/**
 * Run the ontology-researcher workflow and return the suggested schema.
 * AI-backed: allow a generous timeout (the server budgets the multi-agent loop to
 * ~120-160s; give the client headroom above that).
 */
export const generateSuggestion = async (
  request: GenerateSuggestionRequest,
  userEmail?: string
): Promise<SuggestedSchema> => {
  const response = await apiClient.post<SuggestedSchema>(
    `${BASE_URL}/generate`,
    request,
    { headers: userHeaders(userEmail), timeout: 300000 }
  );
  return response.data;
};

/**
 * Instant, LLM-free seed preview for an (industry, domain) — the deterministic
 * curated schema (baseline props + indexes/constraints + DDL, 0 AI-proposed). Used
 * to render a relevant default view immediately on step entry, before the user runs
 * the full AI generation. Fast (<1s); not persisted.
 */
export const previewSuggestion = async (
  request: GenerateSuggestionRequest,
  userEmail?: string
): Promise<SuggestedSchema> => {
  const response = await apiClient.post<SuggestedSchema>(
    `${BASE_URL}/preview`,
    request,
    { headers: userHeaders(userEmail), timeout: 30000 }
  );
  return response.data;
};

/**
 * Fetch a previously generated suggested ontology/schema by id.
 */
export const getSuggestion = async (
  suggestionId: string,
  userEmail?: string
): Promise<SuggestedSchema> => {
  const response = await apiClient.get<SuggestedSchema>(
    `${BASE_URL}/${encodeURIComponent(suggestionId)}`,
    { headers: userHeaders(userEmail) }
  );
  return response.data;
};

/**
 * Fetch the latest persisted suggestion for a case (preferring an accepted one) so
 * the wizard can restore what the user accepted on revisit. Resolves `null` when the
 * case has no saved suggestion (404) — a normal, non-error path.
 */
export const getSuggestionForCase = async (
  caseId: string,
  userEmail?: string
): Promise<SuggestedSchema | null> => {
  try {
    const response = await apiClient.get<SuggestedSchema>(
      `${BASE_URL}/by-case/${encodeURIComponent(caseId)}`,
      { headers: userHeaders(userEmail) }
    );
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

/**
 * Mark a suggestion as accepted (persists the user's choice for restore-on-revisit).
 */
export const acceptSuggestion = async (
  suggestionId: string,
  userEmail?: string
): Promise<SuggestedSchema> => {
  const response = await apiClient.post<SuggestedSchema>(
    `${BASE_URL}/${encodeURIComponent(suggestionId)}/accept`,
    {},
    { headers: userHeaders(userEmail) }
  );
  return response.data;
};

/**
 * List the available seed ontology templates (industry x domain) for the pickers.
 */
export const listOntologyTemplates = async (): Promise<OntologyTemplate[]> => {
  const response = await apiClient.get<OntologyTemplate[]>('/ontology-templates');
  return response.data;
};

/**
 * Auto-detect the (industry, domain) for a case from its company context so the
 * Suggested Ontology pickers can be pre-filled. Deterministic backend resolver;
 * always returns a valid template. Resolves `null` only on an unexpected error so
 * callers can fall back to manual selection.
 */
export const resolveForCase = async (
  caseId: string,
  userEmail?: string
): Promise<ResolvedIndustryDomain | null> => {
  try {
    const response = await apiClient.get<ResolvedIndustryDomain>(
      `${BASE_URL}/resolve-for-case/${encodeURIComponent(caseId)}`,
      { headers: userHeaders(userEmail) }
    );
    return response.data;
  } catch {
    return null;
  }
};

/** Resolve the FULL set of relevant KG-area domains for a case (comprehensive
 *  multi-domain schema). Returns null on any error (caller falls back to single). */
export const resolveDomainsForCase = async (
  caseId: string,
  userEmail?: string
): Promise<ResolvedIndustryDomains | null> => {
  try {
    const response = await apiClient.get<ResolvedIndustryDomains>(
      `${BASE_URL}/resolve-domains-for-case/${encodeURIComponent(caseId)}`,
      { headers: userHeaders(userEmail) }
    );
    return response.data;
  } catch {
    return null;
  }
};

export const ontologySuggestionApi = {
  generateSuggestion,
  resolveDomainsForCase,
  previewSuggestion,
  getSuggestion,
  getSuggestionForCase,
  acceptSuggestion,
  listOntologyTemplates,
  resolveForCase,
};

export default ontologySuggestionApi;
